import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { getAuditLogs } from 'lib/api/admin/auditLogs';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

interface AuditLog {
  id: number;
  method: string;
  name: string;
  ip: string;
  host: string;
  requestOrigin: string;
  entity: string;
  entityId: string;
  previousState: Record<string, any> | undefined;
  newState: Record<string, any> | undefined;
  executorEmail: string;
  executorRoles: string[];
  date: string;
  timestamp: number;
  dateFormatted: string;
  previousStateStr: string;
  newStateStr: string;
}

const getMethodColor = (method: string) => {
  switch (method) {
    case 'POST':
      return 'success';
    case 'PUT':
      return 'warning';
    case 'DELETE':
      return 'error';
    default:
      return 'default';
  }
};

const getMethodLabel = (method: string) => {
  switch (method) {
    case 'POST':
      return 'Creacion';
    case 'PUT':
      return 'Modificacion';
    case 'DELETE':
      return 'Eliminacion';
    default:
      return method;
  }
};

/** Texto para comparar si el campo cambió (una línea, sin formato). */
const valueFingerprint = (val: unknown): string => {
  if (val === undefined || val === null) return '-';
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
};

/** Contenido legible: objetos/arrays con indentación; escalares como JSON.stringify. */
const formatDiffValue = (val: unknown): string => {
  if (val === undefined || val === null) return '-';
  if (typeof val === 'object' && val !== null) {
    try {
      return JSON.stringify(val, null, 2);
    } catch {
      return String(val);
    }
  }
  return JSON.stringify(val);
};

type TDiffSide = 'previous' | 'next';

const DiffValueBlock: React.FC<{
  text: string;
  changed: boolean;
  side: TDiffSide;
}> = ({ text, changed, side }) => {
  const isStructured = text.includes('\n');
  const isPrevious = side === 'previous';

  return (
    <Box
      component={isStructured ? 'pre' : 'div'}
      sx={{
        m: 0,
        minWidth: 0,
        p: isStructured ? 1 : 0.5,
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
        fontSize: isStructured ? '11px' : '12px',
        lineHeight: 1.45,
        whiteSpace: isStructured ? 'pre-wrap' : 'normal',
        wordBreak: 'break-word',
        borderRadius: 1,
        maxHeight: isStructured ? 220 : 'none',
        overflow: isStructured ? 'auto' : 'visible',
        color: changed
          ? isPrevious
            ? '#b71c1c'
            : '#1b5e20'
          : 'text.secondary',
        fontWeight: changed && !isPrevious ? 600 : 400,
        backgroundColor: changed
          ? isPrevious
            ? 'rgba(198, 40, 40, 0.06)'
            : 'rgba(46, 125, 50, 0.06)'
          : 'action.hover',
        border: isStructured ? '1px solid' : 'none',
        borderColor: changed
          ? isPrevious
            ? 'rgba(198, 40, 40, 0.2)'
            : 'rgba(46, 125, 50, 0.2)'
          : 'divider',
      }}
    >
      {text}
    </Box>
  );
};

const renderDiffField = (key: string, oldVal: unknown, newVal: unknown) => {
  const oldStr = valueFingerprint(oldVal);
  const newStr = valueFingerprint(newVal);
  const changed = oldStr !== newStr;
  return (
    <Box
      key={key}
      sx={{
        display: 'grid',
        gridTemplateColumns: '180px 1fr 1fr',
        gap: 1,
        py: 0.5,
        px: 1,
        alignItems: 'start',
        backgroundColor: changed ? '#fff8e1' : 'transparent',
        borderRadius: '4px',
        '&:hover': { backgroundColor: changed ? '#fff3c4' : '#f5f5f5' },
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 'bold', wordBreak: 'break-word' }}
      >
        {key}
      </Typography>
      <DiffValueBlock
        text={formatDiffValue(oldVal)}
        changed={changed}
        side="previous"
      />
      <DiffValueBlock
        text={formatDiffValue(newVal)}
        changed={changed}
        side="next"
      />
    </Box>
  );
};

const AuditLogs: React.FC = () => {
  const dispatch = useDispatch();
  const [startDate, setStartDate] = useState<string | null>(
    dayjs().startOf('day').toISOString(),
  );
  const [endDate, setEndDate] = useState<string | null>(
    dayjs().endOf('day').toISOString(),
  );
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [entityFilter, setEntityFilter] = useState<string>('Todos');
  const [originFilter, setOriginFilter] = useState<string>('Todos');
  const [executorFilter, setExecutorFilter] = useState<string>('');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      query.set('startDate', startDate!);
      query.set('endDate', endDate!);
      if (entityFilter !== 'Todos') query.set('entity', entityFilter);
      if (originFilter !== 'Todos') query.set('requestOrigin', originFilter);
      if (executorFilter) query.set('executorEmail', executorFilter);

      const data = await getAuditLogs(dispatch, query.toString());
      const formattedLogs = (data || []).map((log: any, index: number) => ({
        ...log,
        id: index,
        dateFormatted: log.date
          ? dayjs(log.date).format('DD/MM/YYYY HH:mm')
          : dayjs(log.timestamp).format('DD/MM/YYYY HH:mm'),
        previousStateStr: log.previousState
          ? JSON.stringify(log.previousState)
          : '',
        newStateStr: log.newState ? JSON.stringify(log.newState) : '',
      }));
      setLogs(formattedLogs);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchLogs();
  }, []);

  const handleClickRow = (rowId: number) => {
    const log = filteredLogs.find((l) => l.id === rowId);
    if (log) {
      setSelectedLog(log);
      setOpenDetail(true);
    }
  };

  const handleCloseDetail = () => {
    setOpenDetail(false);
    setSelectedLog(null);
  };

  const searchFilters: Filter[] = [
    {
      label: 'Rango de fechas',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Rango de fechas',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      label: 'Entidad',
      type: 'select',
      value: entityFilter,
      setter: setEntityFilter,
      options: [
        { value: 'Todos', label: 'Todos' },
        { value: 'User', label: 'User' },
        { value: 'Role', label: 'Role' },
      ],
    },
    {
      label: 'Origen',
      type: 'select',
      value: originFilter,
      setter: setOriginFilter,
      options: [
        { value: 'Todos', label: 'Todos' },
        { value: 'API', label: 'API' },
        { value: 'BACKOFFICE', label: 'Backoffice' },
        { value: 'PUBLIC', label: 'Public' },
      ],
    },
    {
      label: 'Ejecutor',
      type: 'text',
      value: executorFilter,
      setter: setExecutorFilter,
    },
  ];

  // Filtering is done server-side, so we use logs directly
  const filteredLogs = logs;

  const dataHeaders = [
    { key: 'dateFormatted', label: 'Fecha' },
    { key: 'name', label: 'Evento' },
    { key: 'entity', label: 'Entidad' },
    { key: 'entityId', label: 'ID Entidad' },
    { key: 'executorEmail', label: 'Ejecutor', type: 'copyable' },
    { key: 'requestOrigin', label: 'Origen' },
    { key: 'ip', label: 'IP' },
    { key: 'previousStateStr', label: 'Estado previo' },
    { key: 'newStateStr', label: 'Estado nuevo' },
  ];

  const buildDiffKeys = () => {
    if (!selectedLog) return [];
    const prev = selectedLog.previousState || {};
    const next = selectedLog.newState || {};
    const allKeys = new Set([...Object.keys(prev), ...Object.keys(next)]);
    return Array.from(allKeys);
  };

  return (
    <div>
      <HeaderWithActions
        title="Aqui podras consultar los logs de auditoria del sistema."
        variant="body1"
        type="subHeader"
        item={
          <GlobalSearcher
            filters={searchFilters}
            triggerSearch={fetchLogs}
            disableSearch={loading}
            maxMonths={1}
          />
        }
      />

      {loading ? (
        <CenteredCircularProgress />
      ) : (
        <EnhancedTable
          dataHeaders={dataHeaders}
          data={filteredLogs}
          rowsPerPageDefault={25}
          handleClickRow={handleClickRow}
          exportToFile="xlsx"
          lastElement
        />
      )}

      <Dialog
        open={openDetail}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Detalle del log</Typography>
            {selectedLog && (
              <Chip
                label={getMethodLabel(selectedLog.method)}
                color={getMethodColor(selectedLog.method) as any}
                size="small"
              />
            )}
          </Box>
          <IconButton onClick={handleCloseDetail}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Box
                sx={{
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  p: 2,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1.5,
                  }}
                >
                  <Typography variant="body2">
                    <strong>Evento:</strong> {selectedLog.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Fecha:</strong>{' '}
                    {selectedLog.date
                      ? dayjs(selectedLog.date).format('DD/MM/YYYY HH:mm:ss')
                      : dayjs(selectedLog.timestamp).format(
                          'DD/MM/YYYY HH:mm:ss',
                        )}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Entidad:</strong> {selectedLog.entity}
                  </Typography>
                  <Typography variant="body2">
                    <strong>ID Entidad:</strong> {selectedLog.entityId}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Ejecutor:</strong> {selectedLog.executorEmail}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Roles:</strong>{' '}
                    {selectedLog.executorRoles?.join(', ') || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Origen:</strong> {selectedLog.requestOrigin}
                  </Typography>
                  <Typography variant="body2">
                    <strong>IP:</strong> {selectedLog.ip}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {selectedLog.previousState || selectedLog.newState ? (
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 'bold', mb: 1 }}
                  >
                    Cambios en la entidad
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '180px 1fr 1fr',
                      gap: 1,
                      py: 1,
                      px: 1,
                      backgroundColor: '#e3f2fd',
                      borderRadius: '4px 4px 0 0',
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                      Campo
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 'bold', color: '#c62828' }}
                    >
                      Estado anterior
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 'bold', color: '#2e7d32' }}
                    >
                      Estado nuevo
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '0 0 4px 4px',
                      maxHeight: '400px',
                      overflow: 'auto',
                    }}
                  >
                    {buildDiffKeys().map((key) =>
                      renderDiffField(
                        key,
                        selectedLog.previousState?.[key],
                        selectedLog.newState?.[key],
                      ),
                    )}
                  </Box>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', py: 3 }}
                >
                  No hay datos de estado disponibles para este log.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogs;
